import { IProject, ISidebarContainers } from 'types'
import { descendantFolderIds, resourceKey } from './tree'

type Sidebar = Pick<IProject, 'hubFolders' | 'sidebarResources'>

/** Whether every folder among `containers` is in `sidebar` (null, the top level, always is). */
export const knowsContainers = (sidebar: Sidebar, containers: (string | null)[]): boolean => {
  const known = new Set(sidebar.hubFolders.map((f) => f.publicId))
  return containers.every((c) => c === null || known.has(c))
}

/**
 * `sidebar` with the `requested` containers' children replaced by what the server read for them
 * (`api.getSidebarContainers`). A removed container goes, with everything inside it. Every other one
 * loses its old children and gets the ones read, and an entry read in one container replaces its old
 * copy wherever that was.
 *
 * `consistent` is false when the result can't be trusted and the whole sidebar should be read instead:
 * a requested container isn't in `sidebar`, or something is left inside a folder that is no longer
 * listed (it went somewhere this read didn't cover).
 */
export const applyContainerDiff = (
  sidebar: Sidebar,
  requested: (string | null)[],
  read: ISidebarContainers
): { sidebar: Sidebar; consistent: boolean } => {
  const removed = new Set(read.removed)
  const replaced = new Set(requested.filter((c) => c === null || !removed.has(c)))
  const gone = new Set<string>()
  for (const id of removed) descendantFolderIds(sidebar.hubFolders, id).forEach((d) => gone.add(d))
  const readFolders = new Set(read.hubFolders.map((f) => f.publicId))
  const readResources = new Set(read.sidebarResources.map(resourceKey))

  const hubFolders = [
    ...sidebar.hubFolders.filter(
      (f) => !gone.has(f.publicId) && !replaced.has(f.parentId) && !readFolders.has(f.publicId)
    ),
    ...read.hubFolders
  ]
  const sidebarResources = [
    ...sidebar.sidebarResources.filter(
      (r) =>
        !(r.folderId !== null && gone.has(r.folderId)) &&
        !replaced.has(r.folderId) &&
        !readResources.has(resourceKey(r))
    ),
    ...read.sidebarResources
  ]

  const listed = new Set(hubFolders.map((f) => f.publicId))
  const isListed = (folderId: string | null): boolean => folderId === null || listed.has(folderId)
  const consistent =
    knowsContainers(sidebar, [...replaced]) &&
    hubFolders.every((f) => isListed(f.parentId)) &&
    sidebarResources.every((r) => isListed(r.folderId))
  return { sidebar: { hubFolders, sidebarResources }, consistent }
}
